import { css } from 'aphrodite';
import React, { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { THEME } from '../../../../components-v2/Theme/theme';
import { mainChartColor } from '../../helpers/chartColors';
import { formatCurrency } from '../../helpers/formatCurrency';
import { formatDealSizeAxisTick } from '../helpers/formatDealSizeAxisTick';
import type { OwnerOrCompanyRow } from '../helpers/opportunityReportingChartTypes';
import { styleSheet } from './DealSizeByOwnerChart/styles';

const CHART_AXIS_TICK = { fontSize: THEME.typography.fontSizes.xxs, fill: THEME.colors.text.main } as const;

interface TooltipPayload {
	payload: OwnerOrCompanyRow & { keyLabel: string; fill: string };
}

function OwnerOrCompanyTooltip({ active, payload }: { active?: boolean; payload?: TooltipPayload[] }) {
	if (!active || !payload?.length) {
		return null;
	}
	const row = payload[0]?.payload;
	if (!row) {
		return null;
	}
	return (
		<div className={css(styleSheet.tooltip)}>
			<div className={css(styleSheet.tooltipTitle)}>{row.label}</div>
			<p className={css(styleSheet.tooltipLine)}>{row.count} opportunities</p>
			<p className={css(styleSheet.tooltipLine)}>{formatCurrency(row.dealSize)}</p>
		</div>
	);
}

interface OwnerOrCompanyBarChartProps {
	data: OwnerOrCompanyRow[];
	isLoading?: boolean;
	className?: string;
	title: string;
	emptyMessage: string;
}

export const OwnerOrCompanyBarChart: React.FC<OwnerOrCompanyBarChartProps> = ({
	data,
	isLoading,
	className,
	title,
	emptyMessage,
}) => {
	const chartData = useMemo(() => data.map(r => ({ ...r, keyLabel: r.label, fill: mainChartColor() })), [data]);

	if (isLoading) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.loading)}>Loading chart data...</div>
			</div>
		);
	}

	if (chartData.length === 0) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.empty)}>{emptyMessage}</div>
			</div>
		);
	}

	return (
		<div className={`${css(styleSheet.container)} ${className || ''}`}>
			<h2 className={css(styleSheet.title)}>{title}</h2>
			<ResponsiveContainer width='100%' height={360}>
				<BarChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 100 }}>
					<CartesianGrid strokeDasharray='3 3' stroke={THEME.colors.border.main} />
					<XAxis
						dataKey='keyLabel'
						stroke={THEME.colors.text.main}
						tick={CHART_AXIS_TICK}
						interval={0}
						angle={-40}
						textAnchor='end'
						height={100}
					/>
					<YAxis stroke={THEME.colors.text.main} tick={CHART_AXIS_TICK} tickFormatter={formatDealSizeAxisTick} />
					<Tooltip content={<OwnerOrCompanyTooltip />} />
					<Bar dataKey='dealSize' radius={[4, 4, 0, 0]}>
						{chartData.map((row, index) => (
							<Cell key={`${row.keyLabel}-${index}`} fill={row.fill} />
						))}
					</Bar>
				</BarChart>
			</ResponsiveContainer>
		</div>
	);
};
